// AutoDocLinks — render plain text with its .md/.pdf references as doors.
//
// P2 doc-editor (DOC-EDIT-CONTRACT.md, Frontend linkification (C), piece 2).
// Takes a string, runs scanDocRefs over it, and renders the plain text with
// each hit as an inline, link-styled <button> that calls openInDocs (the
// index-cached resolve → /docs/<id> in a new tab, or /docs?q= with the
// basename when the reference can't be resolved).
//
// Non-hits pass through untouched — including whitespace, which is preserved
// via whiteSpace: "pre-wrap" on the text spans, so a chat bubble keeps the
// very layout it was typed with.
//
// renderGap: optional renderer for the text BETWEEN and AROUND hits (the
// plain segments). Default keeps the contract's plain-text behavior. ChatPage
// passes its inlineFmt so bold (** ) and inline code (` ) still render inside
// bubbles that also carry doc links — the two renderers compose instead of
// one replacing the other.
//
// Backticked refs (`SYSTEM/x/SKILL.md`) fold the backticks INTO the hit so
// the link replaces the whole chip — the gaps then contain no dangling
// backtick pair, and inlineFmt's re-split can't produce double-backtick
// garbage around the folded hit.
//
// The seam that keeps this honest: openInDocs is handed to the renderer as
// openDoc, defaulting to the real one. Tests (and a future lane that wants
// in-app routing instead of a new tab) pass their own; the component never
// needs a mock of the module system to be testable.

import React, { useCallback, useMemo, useState } from "react";
import { scanDocRefs, openInDocs as defaultOpenInDocs } from "./docLinkify";

// Inline link-styling: underline + the ink color, no layout side effects.
const LINK_CLASS = "underline underline-offset-2 decoration-os-border-strong text-os-ink hover:decoration-os-ink cursor-pointer";

export default function AutoDocLinks({ text, openDoc, renderGap }) {
  const open = openDoc || defaultOpenInDocs;
  const [busy, setBusy] = useState(null);
  const gap = renderGap || ((chunk, key) => (
    <span key={key} className="whitespace-pre-wrap">{chunk}</span>
  ));

  // scanDocRefs is pure; scan once per text, not per render… but the initial
  // render and busy flips re-run it anyway; memo keeps it cheap for long
  // messages with many refs.
  const src = String(text || "");
  const finalHits = useMemo(() => {
    const raw = scanDocRefs(src);
    if (!raw.length) return [];
    // Fold one backtick pair directly around a hit into the hit itself, so
    // the LINK replaces the whole `code chip` and inlineFmt (when passed as
    // renderGap) sees no dangling backticks around it.
    const folded = raw.map((h) => {
      const before = src[h.index - 1];
      const after = src[h.index + h.length];
      let start = h.index;
      let len = h.length;
      if (before === "`") { start -= 1; len += 1; }
      if (after === "`") { len += 1; }
      return { ...h, ref: h.ref, index: start, length: len };
    });
    // Folding can create overlaps (hits sharing one backtick pair); sort and
    // keep the first of any collision so gap slices never go negative.
    const ordered = folded.slice().sort((a, b) => a.index - b.index);
    const out = [];
    let lastEnd = -1;
    ordered.forEach((h) => {
      if (h.index >= lastEnd) {
        out.push(h);
        lastEnd = h.index + h.length;
      }
    });
    return out;
  }, [src]);

  const onDocClick = useCallback(
    (ref) => (e) => {
      e.preventDefault();
      e.stopPropagation();
      // Feedback now: the ref dims while the resolve round-trip runs.
      setBusy(ref);
      const done = () => setBusy(null);
      Promise.resolve(
        open(ref, undefined),
      ).then(done, done);
    },
    [open],
  );

  if (!finalHits.length) {
    return <span data-testid="auto-doc-links">{gap(String(text || ""), 0)}</span>;
  }

  // Slice the text around each hit; gaps render through renderGap, hits as
  // link buttons.
  const parts = [];
  let cursor = 0;
  finalHits.forEach(({ ref, index, length }, i) => {
    if (index > cursor) {
      parts.push(gap(src.slice(cursor, index), `t${i}`));
    }
    parts.push(
      <button
        key={`d${i}`}
        type="button"
        data-doc-ref={ref}
        onClick={onDocClick(ref)}
        style={busy === ref ? { opacity: 0.55 } : undefined}
        className={LINK_CLASS}
        title={`Open in docs: ${ref}`}
      >
        {ref}
      </button>,
    );
    cursor = index + length;
  });
  if (cursor < src.length) {
    parts.push(gap(src.slice(cursor), "tail"));
  }

  return <span data-testid="auto-doc-links">{parts}</span>;
}